import { HttpEventType } from "@angular/common/http";
import { Component, Inject, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { ExportProgress } from "@app/_helpers/progress-interface";
import { createRxGroupWithCache } from "@app/_helpers/rx-value";
import { createRxValue, fromRxValue } from "@app/_helpers/utils";
import { createZodValidator } from "@app/_helpers/zod";
import { AppService } from "@app/app.service";
import { ReportsService } from "@app/pages/reports-page/reports.service";
import { IsDevWindow } from "@env/version";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ApplicationSettingsQuery,
	ComegoService,
	ExportWorkingHoursSchema,
	exportWorkingHoursSchema,
	Logger,
} from "@timeghost/web-api";
import { saveAs } from "file-saver";
import { distinctUntilChanged, map, startWith } from "rxjs";
import z from "zod";

const EXPORT_COLUMNS = {
	date: true,
	start: true,
	end: true,
	pause: true,
	pauseSum: true,
	absences: true,
	actual: true,
	planned: true,
	difference: true,
} as const;

type ScheduleExportColumn = keyof typeof EXPORT_COLUMNS;
type ScheduleExportFileType = "excel" | "csv";
type WorkingHoursExportType = ExportWorkingHoursSchema["type"];
type ScheduleExportColumnGroup = { nameKey: string; columns: ScheduleExportColumn[] };
type ScheduleExportType = {
	name: string;
	type: WorkingHoursExportType;
	fileType: ScheduleExportFileType;
	dev?: boolean;
	beta?: boolean;
};
type ExportWorkingHoursRequest = {
	users: string[] | "all" | "enabled" | "all_with_removed" | "current_user";
	type: WorkingHoursExportType;
	columns?: ScheduleExportColumn[];
	range: {
		start: Date;
		end: Date;
	};
	includedTypes?: string[];
	fillNonTrackedDays: boolean;
	locale: string;
	ampm: boolean;
};

const EXPORT_COLUMNS_ARR = Object.keys(EXPORT_COLUMNS) as ScheduleExportColumn[];
const EXPORT_COLUMN_GROUPS: ReadonlyArray<ScheduleExportColumnGroup> = [
	{ nameKey: "reports.export-columns.groups.time", columns: ["date", "start", "end", "pause", "pauseSum"] },
	{ nameKey: "reports.export-columns.groups.schedule", columns: ["actual", "planned", "difference"] },
	{ nameKey: "reports.export-columns.groups.absences", columns: ["absences"] },
];
const FILE_TYPE_SCHEMA = z.enum(["excel", "csv"]);
const EXPORT_TYPE_SCHEMA = z.enum([
	"reports.exports.excel-download-schedule",
	"reports.exports.excel-download-simple",
	"reports.exports.csv-file",
]);
const COLUMN_TYPE_SCHEMA = z.union([z.literal(0), z.literal(1)]);

interface ScheduleExportsConfig {
	columns?: ScheduleExportColumn[];
	fileType?: ScheduleExportFileType;
}

interface ScheduleExportFormValue {
	fileType: ScheduleExportFileType;
	exportType: string;
	columnType: 0 | 1;
	columns: Record<ScheduleExportColumn, boolean>;
}

const log = new Logger("ScheduleExportDialogComponent");

@ObserveFormGroupErrors()
@UntilDestroy()
@Component({
	selector: "tg-schedule-export-dialog",
	templateUrl: "./schedule-export-dialog.component.html",
	styleUrls: ["./schedule-export-dialog.component.scss"],
})
export class ScheduleExportDialogComponent implements OnInit {
	isLoading: boolean = false;

	readonly fileTypeOptions: ReadonlyArray<{
		value: ScheduleExportFileType;
		title: string;
		description: string;
		icon: string;
	}> = [
		{ value: "excel", title: "Excel", description: ".xlsx", icon: "table_view" },
		{ value: "csv", title: "CSV", description: ".csv", icon: "description" },
	];

	readonly exportColumns = EXPORT_COLUMNS_ARR;
	readonly groupedExportColumns = EXPORT_COLUMN_GROUPS.filter((group) => {
		if (!group.columns.length) return false;
		if (group.nameKey === "reports.export-columns.groups.absences" && !this.data.comego) return false; // not available in project hours
		return true;
	});
	readonly exportTypes: ReadonlyArray<ScheduleExportType> = [
		{ name: "reports.exports.excel-download-schedule", type: "excel_day_format", fileType: "excel" },
		{ name: "reports.exports.excel-download-simple", type: "excel", fileType: "excel", dev: true },
		{ name: "reports.exports.csv-file", type: "csv", fileType: "csv" },
	];
	readonly group = createRxGroupWithCache(
		new FormGroup({
			fileType: new FormControl<ScheduleExportFileType>(this.exportTypes[0].fileType, [
				createZodValidator(FILE_TYPE_SCHEMA),
			]),
			exportType: new FormControl(this.exportTypes[0].name, [createZodValidator(EXPORT_TYPE_SCHEMA)]),
			columnType: new FormControl<0 | 1>(0, [createZodValidator(COLUMN_TYPE_SCHEMA)]),
			columns: new FormGroup({
				...this.exportColumns.reduce(
					(acc, column) => {
						acc[column] = new FormControl<boolean>(true);
						return acc;
					},
					{} as { [key: string]: AbstractControl }
				),
			}),
		}),
		"schedule.exportConfig"
	);

	get formValue(): ScheduleExportFormValue {
		return this.group.getRawValue() as ScheduleExportFormValue;
	}

	readonly formValues$ = fromRxValue(
		this.group.valueChanges.pipe(
			startWith(this.formValue),
			map(() => this.formValue)
		),
		this.formValue
	);

	readonly selectedExportColumns$ = fromRxValue(
		this.formValues$.asObservable().pipe(map((formValue) => this.getFilteredActiveColumns(formValue))),
		this.getFilteredActiveColumns(this.formValue)
	);

	readonly filteredExportTypes$ = fromRxValue(
		this.formValues$.asObservable().pipe(map((formValue) => this.getExportTypesForFileType(formValue.fileType))),
		this.getExportTypesForFileType(this.formValue.fileType)
	);

	progress = createRxValue<ExportProgress>(null);

	constructor(
		private ref: MatDialogRef<ScheduleExportDialogComponent>,
		private appSettingsQuery: ApplicationSettingsQuery,
		private appService: AppService,
		private translateService: TranslateService,
		private comegoService: ComegoService,
		private reportsService: ReportsService,
		@Inject(MAT_DIALOG_DATA)
		private data: {
			comego: boolean;
			filter: {
				endDate: Date;
				startDate: Date;
				users: string[];
				comegoType?: string[];
			};
		}
	) {
		if (!data) throw new Error("MatDialog.open() data prop is required");
		Object.assign(this.data, { comego: false }, data);
	}

	ngOnInit(): void {
		this.ref.updateSize("700px");
		this.initializeFromConfig();
		this.group.controls.fileType.valueChanges
			.pipe(untilDestroyed(this), distinctUntilChanged())
			.subscribe((fileType) => {
				if (!fileType) {
					return;
				}
				const currentExportTypeName = this.group.controls.exportType.value;
				const nextExportTypeName = this.getValidExportTypeName(fileType, currentExportTypeName);
				if (nextExportTypeName !== currentExportTypeName) {
					this.group.patchValue({ exportType: nextExportTypeName }, { emitEvent: false });
				}
			});
	}

	private initializeFromConfig(): void {
		const config = this.config;
		if (!config) {
			return;
		}
		if (config.fileType) {
			const fileType = config.fileType;
			this.group.patchValue(
				{
					fileType,
					exportType: this.getValidExportTypeName(fileType, this.group.controls.exportType.value),
				},
				{ emitEvent: false }
			);
		}
		if (!config.columns?.length) {
			return;
		}
		const activeColumns = new Set(config.columns);
		this.group.patchValue(
			{
				columnType: 1,
				columns: this.exportColumns.reduce(
					(acc, column) => {
						acc[column] = activeColumns.has(column);
						return acc;
					},
					{} as Record<ScheduleExportColumn, boolean>
				),
			},
			{ emitEvent: false }
		);
	}

	private getExportTypesForFileType(fileType: ScheduleExportFileType): ScheduleExportType[] {
		const isDev = IsDevWindow();
		return this.exportTypes.filter(
			(exportType) => exportType.fileType === fileType && (exportType.dev ? isDev : true)
		);
	}

	private getValidExportTypeName(fileType: ScheduleExportFileType, currentName: string): string {
		const currentExportType = this.exportTypes.find((item) => item.name === currentName);
		if (currentExportType?.fileType === fileType) {
			return currentName;
		}
		return this.getExportTypesForFileType(fileType)[0]?.name ?? currentName;
	}

	private getSelectedExportType(exportTypeName: string): ScheduleExportType | null {
		return this.exportTypes.find((item) => item.name === exportTypeName) ?? null;
	}

	private getFilteredActiveColumns(formValue: ScheduleExportFormValue = this.formValue): ScheduleExportColumn[] {
		return this.exportColumns.filter((column) => !!formValue.columns[column]);
	}

	selectAllColumns() {
		this.group.patchValue({
			columns: this.exportColumns.reduce(
				(acc, column) => {
					acc[column] = true;
					return acc;
				},
				{} as Record<ScheduleExportColumn, boolean>
			),
		});
	}

	clearSelectedColumns() {
		this.group.patchValue({
			columns: this.exportColumns.reduce(
				(acc, column) => {
					acc[column] = false;
					return acc;
				},
				{} as Record<ScheduleExportColumn, boolean>
			),
		});
	}

	getErrorObservable(controlName: string, langName: string) {
		return useFormErrorObservable(this)(
			controlName,
			() => this.group.controls[controlName],
			{
				required: () => ({
					content: "errors.required",
					args: { field: langName },
				}),
				zod: (error) => ({
					content: error?.message ?? "errors.invalidValue",
					args: {},
				}),
			},
			(key) => this.translateService.instant(langName ?? key)
		);
	}

	get config(): ScheduleExportsConfig {
		return (this.appSettingsQuery.getValue() as any).schedule?.export;
	}

	set config(value: ScheduleExportsConfig) {
		this.appSettingsQuery.updateByKey("schedule.export", value);
	}

	get closeDisabled() {
		return this.ref.disableClose;
	}

	close() {
		if (!this.closeDisabled) {
			this.ref.close();
		}
	}

	private setSubmittingState(isSubmitting: boolean) {
		this.isLoading = isSubmitting;
		this.ref.disableClose = isSubmitting;
		if (isSubmitting) {
			this.group.disable();
			return;
		}
		this.group.enable();
	}

	private completeSuccessfulExport() {
		this.reportsService.updateDashboardColums();
		this.ref.close();
	}

	private handleSubmitError(error: unknown) {
		log.error("Schedule export failed", error);
		this.setSubmittingState(false);
	}

	private updateProgress(loaded: number, total?: number) {
		const safeTotal = total || loaded || 1;
		this.progress.value = {
			finished: loaded >= safeTotal,
			loaded,
			total: safeTotal,
			percent: safeTotal ? loaded / safeTotal : 0,
		};
	}

	private buildExportFileName(): string {
		const filter = this.data.filter;
		return `timeghost_${filter.startDate.toLocaleDateString()}-${filter.endDate.toLocaleDateString()}`;
	}

	private buildWorkingHoursPayload(
		formValue: ScheduleExportFormValue,
		type: WorkingHoursExportType
	): ExportWorkingHoursRequest {
		const filter = this.data.filter;
		const selectedColumns = this.getFilteredActiveColumns(formValue);
		const hasCustomColumnSelection =
			selectedColumns.length > 0 && selectedColumns.length < this.exportColumns.length;
		return {
			users: filter.users?.length ? filter.users : "all",
			type,
			columns: hasCustomColumnSelection ? selectedColumns : undefined,
			range: {
				start: filter.startDate,
				end: filter.endDate,
			},
			includedTypes: filter.comegoType?.length ? filter.comegoType : undefined,
			fillNonTrackedDays: true,
			locale: this.translateService.currentLang || "de-DE",
			ampm: this.appService.isAMPM,
		};
	}

	private persistConfig(formValue: ScheduleExportFormValue) {
		const selectedColumns = this.getFilteredActiveColumns(formValue);
		const hasCustomColumnSelection =
			selectedColumns.length > 0 && selectedColumns.length < this.exportColumns.length;
		this.config = {
			fileType: formValue.fileType,
			columns: hasCustomColumnSelection ? selectedColumns : [],
		};
	}

	private getExportExtension(type: WorkingHoursExportType): ".csv" | ".xlsx" {
		return type === "csv" ? ".csv" : ".xlsx";
	}

	private downloadWorkingHoursExport(
		options: ExportWorkingHoursRequest,
		onUpdate?: (loaded: number, total?: number) => void
	): Promise<Blob> {
		return new Promise((resolve, reject) => {
			const data = exportWorkingHoursSchema.parse(options);
			const start = data.range.start.toUTC().toISO();
			const end = data.range.end.toUTC().toISO();
			this.comegoService.adapter
				.post(
					"/export_workinghours",
					{
						...data,
						range: {
							start,
							end,
						},
					},
					{
						responseType: "blob",
						reportProgress: true,
						observe: "events",
					}
				)
				.subscribe({
					next: (event) => {
						if (event.type === HttpEventType.DownloadProgress) {
							onUpdate?.(event.loaded, event.total);
							return;
						}
						if (event.type === HttpEventType.Response) {
							const blob = event.body;
							if (!blob) {
								reject(new Error("Empty export response"));
								return;
							}
							onUpdate?.(blob.size, blob.size);
							resolve(blob);
						}
					},
					error: reject,
				});
		});
	}

	async submit() {
		const formValue = this.formValue;
		this.group.updateValueAndValidity();
		if (this.group.invalid) {
			return;
		}
		const exportType = this.getSelectedExportType(formValue.exportType);
		if (!exportType) {
			return;
		}
		this.persistConfig(formValue);
		this.setSubmittingState(true);
		try {
			const payload = this.buildWorkingHoursPayload(formValue, exportType.type);
			const blob = await this.downloadWorkingHoursExport(payload, (loaded, total) => {
				this.updateProgress(loaded, total);
			});
			saveAs(blob, `${this.buildExportFileName()}${this.getExportExtension(exportType.type)}`);
			this.completeSuccessfulExport();
		} catch (error) {
			this.handleSubmitError(error);
		} finally {
			this.progress.value = null;
			this.setSubmittingState(false);
		}
	}
}
